/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */
import {ActionTree} from 'vuex';
import {
    AbstractPlatformInterface, ContributionConfigInterface, ContributionInterface,
    ContributorInterface, EvidenceCreateInterface,
    FileInterface,
    IssueFilterInterface,
    IssueInterface, IssuesConfigInterface,
    IssuesStateInterface, IssueUpdateConfigInterface, MediaCreateInterface, PaginationInterface,
    PcPlatformInterface,
    PsPlatformInterface, S3UploadInterface, VoteConfigInterface, VoteDeleteConfigInterface, VoteInterface,
    XboxPlatformInterface,
} from '@/store/modules/issues/types';
import {RootState} from '@/store/types';
import {api} from '@/services/http';
import {AxiosResponse} from 'axios';
import moment from 'moment';
import ApiException from '@/exceptions/ApiException';
import {state} from '@/store/modules/user-session';

export const actions: ActionTree<IssuesStateInterface, RootState> = {
    /**
     * Fetch issues
     *
     * @param {any} commit
     * @param {
     *      filters: IssueFilterInterface,
     *      stateName: string,
     *      path?: string
     * } issuesConfig
     * @param {IssueFilterInterface} filters
     *
     */
    async fetchIssues({commit}: { commit: any }, issuesConfig: IssuesConfigInterface): Promise<void> {
        const filters: IssueFilterInterface = issuesConfig.filters;
        const parameters: any = {
            'status': 'confirmed' === filters.status ? ['confirmed', 'acknowledged'] : filters.status || undefined,
            'order[createdAt]': filters.orderByDate || undefined,
            'order[vote]': filters.orderByVote || undefined,
            'itemsPerPage': filters.itemsPerPage || undefined,
            'title': filters.searchTerm || undefined,
            'category.game': filters.gameId || undefined,
            'category': filters.category || undefined,
            'customerId': filters.customerId || undefined,
            'platform': filters.platform || undefined,
            'version': filters.version || undefined,
        };

        let path: string = issuesConfig.path || `api/issues?timestamptimestamp=${new Date().getTime()}`;

        if (issuesConfig.showIssueSummary === true) {
            path =  issuesConfig.path || `api/issue_summaries?timestamptimestamp=${new Date().getTime()}`;
            delete parameters['category.game'];
            parameters['category.game.id'] = filters.gameId || undefined;
        }

        if (typeof issuesConfig.path === 'undefined' && issuesConfig.stateName === 'my-contribute') {
            path = `api/issues/contributed`;
            delete parameters['customerId'];
        }

        if (typeof issuesConfig.path === 'undefined' && issuesConfig.stateName === 'my-vote') {
            path = `api/customerVote/voted?customer_id=${filters.customerId}`;
            delete parameters['customerId'];
        }

        return api.axios.get(path, {
            params: parameters,
        }).then((response: AxiosResponse) => {
            const issues: IssueInterface[] = [];
            const total: number = response.data['hydra:totalItems'];

            for (let data of response.data['hydra:member']) {
                let voteId: any = null;

                if (issuesConfig.stateName === 'my-vote') {
                    voteId = data['id'];
                    data = data['issue'];
                }

                const validListStates: Set<string> = new Set(['latest', 'search']);
                if (validListStates.has(issuesConfig.stateName) && null !== data.vote && undefined !== data.vote) {
                    voteId = data.vote.id;
                }

                issues.push({
                    id: data.id,
                    category: {
                        id: data.category['@id'],
                        type: data.category['@type'],
                        gameName: data.category.game_name,
                    },
                    status: data.status,
                    title: data.title,
                    game: (data.category.game_name !== undefined) ? data.category.game_name : data.category.game.name,
                    platforms: data.platforms,
                    voteWeight: data.vote_weight,
                    vote: null === data.vote || undefined === data.vote ? null : {
                        id: voteId,
                        type: data.vote['@type'],
                    },
                    canVote: data.can_vote,
                    tags: data.platforms,
                    isOwner: data.is_owner,
                    version: (!!data.version && !!data.version.name) ? data.version.name : undefined,
                });
            }

            // Pagination
            const view: any = response.data['hydra:view'];
            const pagination: PaginationInterface = {
                id: view['@id'],
                type: view['@type'],
                first: view['hydra:first'],
                last: view['hydra:last'],
                next: view['hydra:next'],
                previous: view['hydra:previous'],
            };

            commit('issuesLoaded', {
                issues,
                stateName: issuesConfig.stateName + `Issues`,
                totalIssues: total,
                pagination,
            });

        }, () => {
            commit('issuesError');
        });
    },

    /**
     * Fetch issue
     *
     * @param {any} commit
     * @param {number} issueId
     */
    async fetchIssue({commit}: { commit: any }, issueId: number): Promise<void> {
        const issueResponse: AxiosResponse =
            await api.axios.get(`api/issues/${issueId}?timestamp=${new Date().getTime()}`);
        const issueDetailsResponse: AxiosResponse =
            await api.axios.get(`api/issue_details?timestamp=${new Date().getTime()}`, {
            params: {
                issue: issueId,
                creatorType: 'creator',
            },
        });

        if (!issueResponse.data) {
            throw new ApiException(404, 'Unable to find issues');
        }

        if (!issueDetailsResponse.data['hydra:member']) {
            throw new ApiException(404, 'Unable to find issues details');
        }

        const issue: any = issueResponse.data;
        const issueDetails: any = issueDetailsResponse.data['hydra:member'][0];

        const platform:
            PcPlatformInterface |
            XboxPlatformInterface |
            undefined = !!issueDetails.platform ? platformFactory(issueDetails) : undefined;


        const files: FileInterface[] = [];

        for (const file of issueDetails.files) {
            files.push({
                id: file.id,
                mimeType: file.mime_type,
                name: file.name,
                presignedUrl: file.presign_url.presigned_url,
            });
        }

        const payload: IssueInterface = {
            id: issue.id,
            category: {
                id: issue.category['@id'],
                type: issue.category['@type'],
                gameName: issue.category.game_name,
            },
            issueDetails: {
                idString: issueDetails['@id'],
                description: issueDetails.description,
                stepsToReproduce: issueDetails.how_to_reproduce,
                platform: platform || undefined,
                mediaFiles: files,
                timeSeen: issueDetails.time_seen,
            },
            status: issue.status,
            title: issue.title,
            game: issue.category.game_name,
            platforms: issue.platforms,
            voteWeight: issue.vote_weight,
            vote: null === issue.vote || undefined === issue.vote ? null : {
                id: issue.vote['id'],
                type: issue.vote['type'],
            },
            canVote: issue.can_vote,
            tags: issue.platforms,
            dateCreated: moment(issue.created_at).format('LLL'),
            dateEdited: undefined === issue.updated_at || issue.updated_at === issue.created_at
                        ? undefined
                        : moment(issue.updated_at).format('LLL'),
            isOwner: issue.is_owner,
            reasonOfResolution: issue.reason_of_resolution,
            version: !!issue.version && !!issue.version.name ? issue.version.name : undefined,
        };
        commit('issueLoaded', payload);
    },

    /**
     * Fetch issue Contributors
     *
     * @param {any} commit
     * @param {ContributionConfigInterface} contributionConfig
     */
    async fetchContributors({commit}: { commit: any }, contributionConfig: ContributionConfigInterface): Promise<void> {
        const path: string = contributionConfig.path || 'api/issue_details';
        return api.axios.get(path, {
            params: {
                'issue': contributionConfig.issueId,
                'creatorType': 'contributor',
                'itemsPerPage': contributionConfig.itemsPerPage || undefined,
                'order[createdAt]': 'desc',
            },
        })
            .then((response: AxiosResponse) => {
                // todo get the correct data
                const contributors: ContributorInterface[] = [];
                const data: any = response.data['hydra:member'];
                const totalItems: number = response.data['hydra:totalItems'];

                for (const contributor of data) {
                    contributors.push({
                        username: contributor.obfuscated_customer_id,
                        isAdmin: contributor.customer_admin,
                        description: contributor.description,
                        canReproduce: contributor.can_reproduce,
                        howToReproduce: contributor.how_to_reproduce,
                        timeSeen: contributor.time_seen,
                        dateCreated: moment(contributor.created_at).format('LLL'),
                        mediaFiles: {
                            images: contributor.files,
                            links: [
                                '',
                            ],
                        },
                    });
                }

                // Pagination
                const view: any = response.data['hydra:view'];
                const pagination: PaginationInterface = {
                    id: view['@id'],
                    type: view['@type'],
                    first: view['hydra:first'],
                    last: view['hydra:last'],
                    next: view['hydra:next'],
                    previous: view['hydra:previous'],
                };

                commit('contributorsLoaded', {
                    contributors,
                    totalItems,
                    pagination,
                });
            }, () => {
                commit('contributorsError');
            });
    },

    /**
     * Vote issue
     * @param {any} commit
     * @param {VoteInterface} voteDataConfig
     * @return {Promise<void>}
     */
    async voteIssue({commit}: { commit: any }, voteDataConfig: VoteConfigInterface): Promise<void> {
        return api.axios.post('api/customer_votes', voteDataConfig.voteData)
            .then((response: AxiosResponse) => {
                commit('updateVote', {
                    issueId: voteDataConfig.issueId,
                    stateName: voteDataConfig.stateName,
                    vote: {id: '/api/customer_votes/' + response.data.id},
                });
            });
    },

    /**
     * Delete vote of an issue
     *
     * @param {any} commit
     * @param {VoteInterface} voteDataConfig
     * @param {Promise<void>}
     *
     */
    async deleteVote({commit}: { commit: any }, voteDataConfig: VoteDeleteConfigInterface): Promise<void> {
        return api.axios.delete(voteDataConfig.voteId)
            .then(() => {
                commit('updateVote', {
                    issueId: voteDataConfig.issueId,
                    stateName: voteDataConfig.stateName,
                    vote: voteDataConfig.vote,
                });
            });
    },

    /**
     * Create a contribution for an issue.
     * @param {any} commit
     * @param {ContributionInterface} contribution
     */
    async createContribution({commit}: { commit: any }, contribution: ContributionInterface): Promise<number> {
        contribution.creator_type = 'contributor';
        return api.axios.post('api/issue_details', contribution).then((response: AxiosResponse) => {
            return response.data.id;
        });
    },

    /**
     * Create a media item (with pre-signed url).
     * @param {any} commit
     * @param {MediaCreateInterface} media
     * @return {Promise<any>}
     */
    async createMedia({commit}: { commit: any }, media: MediaCreateInterface): Promise<FileInterface> {
        return api.axios.post('/api/files', media).then((response: AxiosResponse) => {
            return {
                id: response.data['@id'],
                mimeType: response.data['mime_type'],
                name: response.data['name'],
                presignedUrl: response.data['presign_url']['presigned_url'],
            };
        });
    },

    /**
     *
     * @param {any} commit
     * @param {S3UploadInterface} file
     * @return {Promise<void>}
     */
    async uploadToS3({commit}: { commit: any }, file: S3UploadInterface): Promise<any> {
        return api.axios.put(file.preSignedUrl, file.file, {
            params: {
                skipAuthorization: true,
            },
            headers: {
                'Content-Type': file.file.type,
            },
        });
    },

    /**
     * Create evidence.
     * This will link evidence to an issue.
     * @param {any} commit
     * @param {EvidenceCreateInterface} evidence
     * @return {Promise<any>}
     */
    async createEvidence({commit}: { commit: any }, evidence: EvidenceCreateInterface): Promise<any> {
        return api.axios.post('/api/issue_details/' + evidence.issueDetailsId + '/evidence', {
            files: evidence.fileIdsData,
        });
    },

    // maybe use it to update currentIssue for edit
    // /**v
    //  * Update current issue
    //  *
    //  * @param {any} commit
    //  * @param {IssueInterface | undefined} issue
    //  */
    // updateCurrentIssue({commit}: { commit: any }, issue: IssueInterface | undefined): void {
    //     commit('updateCurrentIssue', issue);
    // },

    /**
     * Update search term
     *
     * @param {any} commit
     * @param {string} term
     */
    updateSearchTerm({commit}: { commit: any }, term: string): void {
        commit('updateSearchTerm', term);
    },

    /**
     * Update/Edit Issue
     *
     * @param {any} commit
     * @param {IssueUpdateConfigInterface} issueConfig
     * @return {Promise<boolean>}
     */
    async updateIssue({commit}: { commit: any }, issueConfig: IssueUpdateConfigInterface): Promise<boolean> {
        try {
            await api.axios.put(`api/issues/${issueConfig.issueId}`, issueConfig.titleData, {
                headers: {
                    Authorization: state.session.token,
                },
                params: {
                    skipAuthorization: true,
                },
            });
            await api.axios.put(
                issueConfig.issueDetailsIdString,
                issueConfig.detailsData,
                {
                    headers: {
                        Authorization: state.session.token,
                    },
                    params: {
                        skipAuthorization: true,
                    },
                },
            );
            return true;
        } catch (e) {
            return false;
        }
    },

    /**
     * Delete Issue
     *
     * @param {any} commit
     * @param {number} issueId
     *
     * @return {Promise<AxiosResponse>}
     */
    async deleteIssue({commit}: { commit: any }, issueId: number ): Promise<AxiosResponse> {
        return api.axios.delete(`api/issues/${issueId}`, {
            headers: {
                Authorization: state.session.token,
            },
            params: {
                skipAuthorization: true,
            },
        });
    },

};

/**
 * Factory function to return a type of Platform or undefined if data is incorrect
 *
 * @param data
 */
function platformFactory(data: any):
    PcPlatformInterface |
    XboxPlatformInterface |
    PsPlatformInterface | undefined {

    const abstractPlatform: AbstractPlatformInterface = {
        id: data.platform['@id'],
        type: data.platform['@type'],
        region: data.platform.region,
        internetProvider: data.platform.internet_provider,
        internetSpeed: data.platform.internet_speed,
        additionalTechnicalInfo: data.platform.additional_technical_info,
    };

    switch (data.platform['@type']) {
        case 'PcPlatform' :
            return (Object.assign(abstractPlatform, {
                os: data.platform.os,
                processor: data.platform.processor,
                memory: data.platform.memory,
                directXVersion: data.platform.direct_x_version,
                graphicCardChipType: data.platform.graphic_card_chip_type,
                loadedVia: data.platform.loaded_via,
            }) as PcPlatformInterface);
        case 'PsPlatform' :
            return (Object.assign(abstractPlatform, {
                platformType: data.platform.type,
            }) as PsPlatformInterface);
        case 'XboxPlatform' :
            return (Object.assign(abstractPlatform, {
                platformType: data.platform.type,
            }) as XboxPlatformInterface);
    }

    return undefined;
}
